import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MonthSelector from './components/MonthSelector';
import SummaryCard from './components/SummaryCard';
import RecentActivity from './components/RecentActivity';
import DashboardQuickActions from '../../components/ui/DashboardQuickActions';
import ImportTransactionsModal from './components/ImportTransactionsModal';
import { dashboardService } from '../../services/dashboardService';
import { realtimeService } from '../../services/realtimeService';

import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getCurrentDateInBarbados } from '../../utils/timezone';
import Icon from '../../components/AppIcon';


const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const currentDate = getCurrentDateInBarbados();
  const [selectedMonth, setSelectedMonth] = useState(currentDate?.getMonth());
  const [financialData, setFinancialData] = useState({
    income: 0,
    expense: 0,
    net: 0,
    transactionCount: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realtimeChannels, setRealtimeChannels] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [activeSection, setActiveSection] = useState('overview'); // 'overview' or 'import'

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [selectedMonth, user]);

  // Enhanced real-time subscription for all financial data
  useEffect(() => {
    if (!user) return;

    const channels = realtimeService?.subscribeToAllFinancialData({
      onTransactionChange: () => {
        // Reload dashboard data when transactions change
        loadDashboardData();
      },
      onBudgetChange: () => {
        // Reload dashboard data when budgets change
        loadDashboardData();
      },
      onAccountChange: () => {
        // Reload dashboard data when account balances change
        loadDashboardData();
      }
    });

    setRealtimeChannels(channels);

    return () => {
      realtimeService?.unsubscribeAll(channels);
    };
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const currentYear = getCurrentDateInBarbados()?.getFullYear();
      const [summary, activities] = await Promise.all([
        dashboardService?.getFinancialSummary(selectedMonth, currentYear),
        dashboardService?.getRecentActivity(6)
      ]);

      setFinancialData(summary);
      setRecentActivities(activities);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    return '$';
  };

  const currencySymbol = getCurrencySymbol('BBD');

  const handleImportComplete = () => {
    setShowImportModal(false);
    setActiveSection('overview');
    loadDashboardData();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-16"></div>
          <p className="text-muted-foreground font-caption">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - Rovyn Budget | Your Financial Command Center</title>
        <meta name="description" content="View your comprehensive financial overview with income, expenses, net balance, and recent transactions. Manage your budget efficiently with Rovyn Budget's premium dashboard." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20">
          <div className="mx-auto px-4 md:px-6 lg:px-24 py-32 md:py-40 lg:py-48">
            {/* Header Section */}
            <div className="mb-32 md:mb-40">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8">
                Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground font-caption">
                Your financial overview at a glance
              </p>
            </div>

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-16 mb-32">
                <p className="text-sm text-error font-caption">{error}</p>
              </div>
            )}

            {/* Month Selector - Prominent Position */}
            <div className="mb-32">
              <MonthSelector selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />
            </div>

            {/* Section Tabs */}
            <div className="mb-32 border-b border-border">
              <div className="flex gap-4">
                <button
                  onClick={() => setActiveSection('overview')}
                  className={`px-20 py-14 font-caption text-sm font-medium border-b-2 transition-all duration-200 ${
                    activeSection === 'overview' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <div className="flex items-center gap-8">
                    <Icon name="LayoutDashboard" className="w-16 h-16" />
                    Overview
                  </div>
                </button>
                <button
                  onClick={() => setActiveSection('import')}
                  className={`px-20 py-14 font-caption text-sm font-medium border-b-2 transition-all duration-200 ${
                    activeSection === 'import' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <div className="flex items-center gap-8">
                    <Icon name="Upload" className="w-16 h-16" />
                    Import Documents
                  </div>
                </button>
              </div>
            </div>

            {/* Overview Section */}
            {activeSection === 'overview' && (
              <>
                {/* Monthly Summary Section - Top Priority */}
                <div className="mb-40">
                  <div className="mb-20">
                    <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                      Monthly Summary
                    </h2>
                    <p className="text-sm text-muted-foreground font-caption">
                      Your financial snapshot for the selected month
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-20 md:gap-24">
                    <SummaryCard
                      title="Total Income"
                      amount={financialData?.income}
                      currency={currencySymbol}
                      type="income"
                      trend={0}
                      comparison={0}
                    />
                    <SummaryCard
                      title="Total Expenses"
                      amount={financialData?.expense}
                      currency={currencySymbol}
                      type="expense"
                      trend={0}
                      comparison={0}
                    />
                    <SummaryCard
                      title="Net Balance"
                      amount={financialData?.net}
                      currency={currencySymbol}
                      type="net"
                      trend={0}
                      comparison={0}
                    />
                    <SummaryCard
                      title="Transactions"
                      amount={financialData?.transactionCount}
                      currency=""
                      type="transactions"
                      trend={0}
                      comparison={0}
                    />
                  </div>
                </div>

                {/* Quick Actions Section - Secondary Priority */}
                <div className="mb-40">
                  <div className="mb-20">
                    <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                      Quick Actions
                    </h2>
                    <p className="text-sm text-muted-foreground font-caption">
                      Common tasks and shortcuts
                    </p>
                  </div>
                  <DashboardQuickActions />
                </div>

                {/* Recent Activity Section - Details Below */}
                <div>
                  <div className="mb-20">
                    <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
                      Recent Activity
                    </h2>
                    <p className="text-sm text-muted-foreground font-caption">
                      Your latest transactions and updates
                    </p>
                  </div>
                  <RecentActivity activities={recentActivities} />
                </div>
              </>
            )}

            {/* Import Section */}
            {activeSection === 'import' && (
              <div className="space-y-32">
                <div className="bg-muted/30 rounded-xl p-32 md:p-40 border border-border/50">
                  <div className="max-w-2xl mx-auto text-center">
                    <div className="mx-auto w-80 h-80 bg-primary/10 rounded-full flex items-center justify-center mb-24">
                      <Icon name="Upload" className="w-40 h-40 text-primary" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-12">
                      Import Transactions
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground font-caption mb-32 leading-relaxed">
                      Upload CSV or PDF files to automatically import your transactions. We'll help you map the columns and review before importing.
                    </p>
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="inline-flex items-center gap-10 px-28 py-16 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-200 font-caption text-sm font-semibold shadow-sm hover:shadow-md active:scale-[0.98]"
                    >
                      <Icon name="FileText" className="w-18 h-18" />
                      Select File to Import
                    </button>
                  </div>
                </div>

                <div className="bg-background border border-border rounded-xl p-32">
                  <h4 className="text-base font-heading font-semibold text-foreground mb-16">
                    Supported Formats
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div className="flex items-start gap-12">
                      <div className="w-32 h-32 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                        <Icon name="FileText" className="w-16 h-16 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-caption font-medium text-foreground mb-4">CSV Files</p>
                        <p className="text-xs text-muted-foreground font-caption">
                          Comma-separated files with or without headers. We'll auto-detect the format.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-12">
                      <div className="w-32 h-32 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                        <Icon name="FileText" className="w-16 h-16 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-caption font-medium text-foreground mb-4">PDF Files</p>
                        <p className="text-xs text-muted-foreground font-caption">
                          Bank statements and transaction reports in PDF format.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background border border-border rounded-lg p-24">
                  <h4 className="text-base font-heading font-semibold text-foreground mb-16">
                    Import Process
                  </h4>
                  <div className="space-y-12">
                    <div className="flex items-start gap-12">
                      <div className="w-24 h-24 bg-primary text-primary-foreground rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        1
                      </div>
                      <div>
                        <p className="text-sm font-caption font-medium text-foreground mb-4">Upload File</p>
                        <p className="text-xs text-muted-foreground font-caption">
                          Select your CSV or PDF file containing transactions
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-12">
                      <div className="w-24 h-24 bg-primary text-primary-foreground rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        2
                      </div>
                      <div>
                        <p className="text-sm font-caption font-medium text-foreground mb-4">Map Columns</p>
                        <p className="text-xs text-muted-foreground font-caption">
                          Match your file columns to transaction fields (date, amount, description)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-12">
                      <div className="w-24 h-24 bg-primary text-primary-foreground rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        3
                      </div>
                      <div>
                        <p className="text-sm font-caption font-medium text-foreground mb-4">Review & Edit</p>
                        <p className="text-xs text-muted-foreground font-caption">
                          Review imported transactions and edit categories before saving
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {showImportModal && (
        <ImportTransactionsModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </>
  );
};

export default Dashboard;